import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

/**
 * PageStats - Standardized statistics display component
 * 
 * Used for displaying headline statistics in headers or dashboard sections.
 * Supports various data types, trends, and interactive states.
 * 
 * Features:
 * - Multiple layout options (grid, row, card)
 * - Trend indicators with icons
 * - Animated number transitions
 * - Color-coded values
 * - Click interactions for drill-down
 * - Loading states
 * 
 * @example
 * <PageStats
 *   layout="grid"
 *   stats={[
 *     {
 *       label: "Total Users",
 *       value: 156,
 *       trend: { value: "+12%", direction: "up", color: "success" },
 *       icon: UserGroupIcon,
 *       onClick: () => navigate('/users')
 *     },
 *     {
 *       label: "Active Today",
 *       value: 89,
 *       trend: { value: "-5%", direction: "down", color: "error" },
 *       icon: ClockIcon
 *     }
 *   ]}
 * />
 */
const PageStats = ({
  stats = [],
  layout = 'grid', // 'grid', 'row', 'card'
  size = 'default', // 'sm', 'default', 'lg'
  variant = 'default', // 'default', 'gradient', 'minimal'
  loading = false,
  className = '',
  onClick = null
}) => {
  if (!stats.length && !loading) return null;

  // Layout classes
  const getLayoutClasses = () => {
    switch (layout) {
      case 'row':
        return 'flex flex-wrap gap-2 sm:gap-4';
      case 'card':
        return 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6';
      case 'grid':
      default:
        return 'grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4';
    }
  };

  // Size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'p-2 sm:p-3';
      case 'lg':
        return 'p-4 sm:p-6';
      case 'default':
      default:
        return 'p-3 sm:p-4';
    }
  };

  // Variant styling
  const getVariantClasses = () => {
    switch (variant) {
      case 'gradient':
        return 'bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20';
      case 'minimal':
        return 'border-base-200';
      case 'default':
      default:
        return 'bg-base-100 border-base-300';
    }
  };

  // Get trend icon
  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <ArrowTrendingUpIcon className="w-4 h-4" />;
      case 'down':
        return <ArrowTrendingDownIcon className="w-4 h-4" />;
      case 'neutral':
      default:
        return <MinusIcon className="w-4 h-4" />;
    }
  };

  // Get trend color
  const getTrendColor = (color, direction) => {
    if (color) return `text-${color}`;
    switch (direction) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-error';
      default:
        return 'text-base-content/70';
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className={getLayoutClasses()}>
        {[...Array(layout === 'row' ? 3 : 4)].map((_, index) => (
          <div
            key={index}
            className={`${getVariantClasses()} ${getSizeClasses()} rounded-xl border ${getSizeClasses()}`}
          >
            <div className="animate-pulse">
              <div className="h-4 bg-base-300 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-base-300 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`${getLayoutClasses()} ${className}`}>
      {stats.map((stat, index) => {
        const hasClickAction = stat.onClick || onClick;
        
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={hasClickAction ? { scale: 1.02 } : {}}
            onClick={() => {
              if (stat.onClick) {
                stat.onClick(stat);
              } else if (onClick) {
                onClick(stat);
              }
            }}
            className={`
              ${getVariantClasses()} 
              ${getSizeClasses()} 
              rounded-xl border transition-all duration-200
              ${hasClickAction ? 'cursor-pointer hover:shadow-lg' : ''}
              ${stat.color ? `border-${stat.color}/30` : ''}
            `}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {stat.icon && (
                  <div className={`text-base-content/60 ${stat.iconColor || ''}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                )}
                <span className="text-sm text-base-content/70 font-medium">
                  {stat.label}
                </span>
              </div>
              
              {stat.badge && (
                <span className={`badge badge-sm ${stat.badge.color || 'badge-neutral'}`}>
                  {stat.badge.text}
                </span>
              )}
            </div>

            <div className="flex items-end justify-between">
              <div>
                <div className={`text-lg font-bold text-base-content sm:text-2xl ${
                  stat.valueColor ? `text-${stat.valueColor}` : ''
                }`}>
                  {stat.formattedValue || stat.value}
                </div>
                {stat.subtitle && (
                  <div className="text-xs text-base-content/60 mt-1">
                    {stat.subtitle}
                  </div>
                )}
              </div>

              {stat.trend && (
                <div className={`flex items-center gap-1 text-sm ${getTrendColor(stat.trend.color, stat.trend.direction)}`}>
                  {getTrendIcon(stat.trend.direction)}
                  <span className="font-medium">{stat.trend.value}</span>
                </div>
              )}
            </div>

            {stat.description && (
              <div className="mt-3 pt-3 border-t border-base-200">
                <p className="text-xs text-base-content/60">{stat.description}</p>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

// Preset configurations for common use cases
PageStats.presets = {
  // Dashboard overview stats
  dashboard: (data) => ({
    layout: 'grid',
    variant: 'gradient',
    stats: [
      {
        label: 'Total Students',
        value: data.totalStudents,
        icon: UserGroupIcon,
        trend: { value: '+5%', direction: 'up' }
      },
      {
        label: 'Active Today',
        value: data.activeToday,
        icon: ClockIcon,
        trend: { value: '+2%', direction: 'up' }
      },
      {
        label: 'Total Mentions',
        value: data.totalMentions,
        icon: DocumentTextIcon,
        trend: { value: '-3%', direction: 'down' }
      },
      {
        label: 'Engagement Rate',
        value: `${data.engagementRate}%`,
        icon: ChartBarIcon,
        trend: { value: '+8%', direction: 'up' }
      }
    ]
  }),

  // Minimal row stats for headers
  header: (data) => ({
    layout: 'row',
    size: 'sm',
    variant: 'minimal',
    stats: data.map(item => ({
      label: item.label,
      value: item.value,
      trend: item.trend
    }))
  }),

  // Card-style stats for detailed views
  detailed: (data) => ({
    layout: 'card',
    size: 'lg',
    variant: 'default',
    stats: data
  })
};

export default PageStats;